import React, { SyntheticEvent, useEffect, useState } from "react";
import scannedMediaObjectRowStyle from "./scannedMediaObjectRowStyle";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MuiLink from "@mui/material/Link";
import LegalbirdAutoComplete from "../AutoComplete/LegalbirdAutoComplete";
import _ from "lodash";
import { ALGOLIA_INDEX } from "../../config/_entrypoint";
import ApiClient from "../../services/ApiClient";
import { Delete, FeedbackOutlined, FileDownloadOutlined } from "@mui/icons-material";
import { getCaseLink } from "../../services/Product/ProductService/productService";
import { Link as RouterLink } from "react-router-dom";
import { useActionIdentifier } from "../../provider/ActionIdentifierProvider";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import ButtonLoading from "../Button/ButtonLoading";
import SearchBar from "../Algolia/SearchBar";
import CaseHit from "../Case/CaseEntityPicker/Hit/CaseHit";
import LabeledSelect from "../Select/LabeledSelect";
import { userHasOneOfTheseRoles } from "../../services/backofficeUserService";
import { Roles } from "../../types/BackofficeUser";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { MediaObject, ScanMetaData } from "../../types/MediaObject";
import { CaseHit as CaseHitType } from "../Case/CaseEntityPicker/Hit/hitType";
import { AutoCompleteOptionWithValue } from "../../types/AutoCompleteOptionWithValue";
import { useDocumentClassLabelAi } from "../../provider/DocumentClassLabelAiProvider";
import DisplayableMediaObject from "../MediaObject/DisplayableMediaObject";
import { translate } from "../../services/Translations/translatorService";
import { NO_LABEL_ACTION_IDENTIFIERS } from "../../services/mediaObjectService";
import dayjs from "dayjs";
import { requiredFieldDefaultFieldBased } from "../../services/reactHookFormValidationRules";

type ScannedMediaObjectRowProps = {
  mediaObject: MediaObject;
  refreshList: Function;
};

export default function ScannedMediaObjectRow({ mediaObject, refreshList }: ScannedMediaObjectRowProps) {
  const currentUser = useCurrentUser();

  const {
    getDocumentClassLabelsAiByProductClass,
    prepareAndSortLabels,
    getPreparedDocumentClassLabelsAiByProductAndLabelValue,
    isLoading: isLoadingDocumentClassLabels,
  } = useDocumentClassLabelAi();
  const [documentClassLabelAi, setDocumentClassLabelAi] = useState<AutoCompleteOptionWithValue | null>(null);
  const [documentClassLabelAiOptions, setDocumentClassLabelAiOptions] = useState<any[]>([]);
  const [isLabelPrefilledFromDatabase, setIsLabelPrefilledFromDatabase] = useState(true);

  const {
    getActionIdentifiersByLabel,
    prepareAndSortActionIdentifiers,
    getPreparedActionIdentifierOptionByLabelAndIdentifier,
    isLoading: isLoadingActionIdentifiers,
  } = useActionIdentifier();
  const [actionIdentifier, setActionIdentifier] = useState<AutoCompleteOptionWithValue | null>(null);
  const [actionIdentifierOptions, setActionIdentifierOptions] = useState<any[]>([]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeField, setActiveField] = useState("");
  const [fileDescription, setFileDescription] = useState(mediaObject.description);
  const { productId, productType: productClassName, caseLabel, reference } = mediaObject.scanMetaData ?? {};

  // On first load fetch the label/identifier data based on mediaObject
  useEffect(() => {
    if (isLoadingDocumentClassLabels || isLoadingActionIdentifiers) {
      return;
    }
    const currentMediaObjectLabel = mediaObject.documentClassLabelAi || mediaObject.scanMetaData?.labelPredicted;
    const currentMediaObjectProductClassName = mediaObject.product || mediaObject.scanMetaData?.productType;
    if (!!currentMediaObjectProductClassName) {
      let productSpecificLabels = prepareAndSortLabels(
        getDocumentClassLabelsAiByProductClass(currentMediaObjectProductClassName)
      );
      setDocumentClassLabelAiOptions(productSpecificLabels);
      if (!currentMediaObjectLabel) {
        return;
      }
      let savedLabel = getPreparedDocumentClassLabelsAiByProductAndLabelValue(
        currentMediaObjectProductClassName,
        currentMediaObjectLabel
      );
      setDocumentClassLabelAi(savedLabel);
      let labelSpecificIdentifiers = prepareAndSortActionIdentifiers(
        getActionIdentifiersByLabel(currentMediaObjectLabel)
      );
      setActionIdentifierOptions(labelSpecificIdentifiers);

      const currentMediaObjectIdentifier =
        mediaObject.actionIdentifier ?? mediaObject.scanMetaData?.actionIdentifier;
      if (!!currentMediaObjectIdentifier) {
        let savedIdentifier = getPreparedActionIdentifierOptionByLabelAndIdentifier(
          currentMediaObjectLabel,
          currentMediaObjectIdentifier
        );
        setActionIdentifier(savedIdentifier);
      } else {
        // If there is only one identifier for the label, set it as default, if there is no savedIdentifier
        if (labelSpecificIdentifiers.length === 1) {
          setActionIdentifier(labelSpecificIdentifiers[0]);
        } else {
          setActionIdentifier(null);
        }
      }
    }
  }, [isLoadingDocumentClassLabels, isLoadingActionIdentifiers]);

  // For label selection
  useEffect(() => {
    if (productClassName && !isLoadingDocumentClassLabels) {
      let productSpecificLabels = prepareAndSortLabels(getDocumentClassLabelsAiByProductClass(productClassName));
      setDocumentClassLabelAiOptions(productSpecificLabels);
    }
  }, [productClassName]);

  // For actionIdentifier selection
  useEffect(() => {
    if (documentClassLabelAi && !isLoadingActionIdentifiers) {
      let labelSpecificIdentifiers = prepareAndSortActionIdentifiers(
        getActionIdentifiersByLabel(documentClassLabelAi.value)
      );
      setActionIdentifierOptions(labelSpecificIdentifiers);
      if (isLabelPrefilledFromDatabase) {
        return;
      }

      if (documentClassLabelAi.value === "NO_LABEL") {
        setActionIdentifier(labelSpecificIdentifiers.find((item) => item.value === "OPS_ACTIVITY") ?? null);
        return;
      }

      if (labelSpecificIdentifiers.length === 1) {
        setActionIdentifier(labelSpecificIdentifiers[0]);
      } else {
        setActionIdentifier(null);
      }
    }
  }, [documentClassLabelAi]);

  const onCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    let scanMetaDataCopy = { ...mediaObject.scanMetaData! };
    scanMetaDataCopy.checkedDate = event.target.checked ? dayjs().format() : null;
    scanMetaDataCopy.checkedBy = event.target.checked ? currentUser["@id"] : null;
    await handleScanMetaDataSave(scanMetaDataCopy);
    refreshList();
    setIsLoading(false);
  };

  const onHitClick = async (
    { product, productId, reference, gender, givenName, familyName }: CaseHitType,
    event?: SyntheticEvent<HTMLElement>
  ) => {
    if (!event) {
      return;
    }
    event.preventDefault();
    setIsLoading(true);
    let scanMetaDataCopy = { ...mediaObject.scanMetaData! };
    scanMetaDataCopy.productType = product;
    scanMetaDataCopy.productId = productId;
    scanMetaDataCopy.reference = reference;
    scanMetaDataCopy.caseLabel = translate("person.gender.values." + gender) + " " + givenName + " " + familyName;
    await handleScanMetaDataSave(scanMetaDataCopy);
    refreshList();
    setIsLoading(false);
  };

  const handleOpenDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const resetActiveField = () => {
    setActiveField("");
  };

  const handleDeleteMediaObject = async () => {
    setIsLoading(true);
    await ApiClient.patch(mediaObject["@id"], {
      body: JSON.stringify({
        deleted: true,
      }),
    });
    refreshList();
    setIsLoading(false);
    setDeleteModalOpen(false);
  };

  const handleDocumentClassLabelAiValueChange = async (
    _e: React.SyntheticEvent,
    value: AutoCompleteOptionWithValue | null
  ) => {
    if (!value) {
      return;
    }
    setIsLoading(true);
    setIsLabelPrefilledFromDatabase(false);
    setDocumentClassLabelAi(value);

    let payload: Partial<MediaObject> = {
      documentClassLabelAi: value.value,
      actionIdentifier: null,
    };

    let labelSpecificIdentifiers = prepareAndSortActionIdentifiers(getActionIdentifiersByLabel(value.value));
    if (value.value === "NO_LABEL") {
      payload.actionIdentifier = labelSpecificIdentifiers.find((item) => item.value === "OPS_ACTIVITY")?.value ?? null;
    } else if (labelSpecificIdentifiers.length === 1) {
      payload.actionIdentifier = labelSpecificIdentifiers[0].value;
    }

    await ApiClient.patch(mediaObject["@id"], {
      body: JSON.stringify(payload),
    });

    await refreshList();
    resetActiveField();
    setIsLoading(false);
  };

  const handleActionIdentifierValueChange = async (
    _e: React.SyntheticEvent,
    value: AutoCompleteOptionWithValue | null
  ) => {
    if (!value) {
      return;
    }
    setIsLoading(true);
    setActionIdentifier(value);

    let payload: Partial<MediaObject> = {
      actionIdentifier: value.value,
      documentClassLabelAi: documentClassLabelAi?.value,
    };

    if (!NO_LABEL_ACTION_IDENTIFIERS.includes(value.value)) {
      payload.description = "";
      setFileDescription("");
    }

    await ApiClient.patch(mediaObject["@id"], {
      body: JSON.stringify(payload),
    });

    await refreshList();
    resetActiveField();
    setIsLoading(false);
  };

  const handleSaveDescription = async () => {
    setIsLoading(true);
    await ApiClient.patch(mediaObject["@id"], {
      body: JSON.stringify({
        description: fileDescription,
      }),
    });
    refreshList();
    resetActiveField();
    setIsLoading(false);
  };

  const handleScanMetaDataSave = async (scanMetaData: ScanMetaData) => {
    await ApiClient.patch(mediaObject["@id"], {
      body: JSON.stringify({
        scanMetaData: scanMetaData,
        documentClassLabelAi: documentClassLabelAi?.value,
        actionIdentifier: actionIdentifier?.value,
      }),
    });
  };

  const handleShowInGalleryChange = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    let scanMetaDataCopy = { ...mediaObject.scanMetaData! };
    scanMetaDataCopy.showInGallery = target.value === "true";
    await handleScanMetaDataSave(scanMetaDataCopy);
    await refreshList();
    resetActiveField();
    setIsLoading(false);
  };

  const handleSendMessageToCustomerChange = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    let scanMetaDataCopy = { ...mediaObject.scanMetaData! };
    const sendMessageToCustomer = target.value === "true";
    scanMetaDataCopy.sendMessageToCustomer = sendMessageToCustomer;
    if (sendMessageToCustomer) {
      scanMetaDataCopy.showInGallery = true;
    }
    await handleScanMetaDataSave(scanMetaDataCopy);
    await refreshList();
    resetActiveField();
    setIsLoading(false);
  };

  const handlePreventDefaultActionsChange = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    let scanMetaDataCopy = { ...mediaObject.scanMetaData! };
    scanMetaDataCopy.preventDefaultActions = target.checked;
    await handleScanMetaDataSave(scanMetaDataCopy);
    await refreshList();
    resetActiveField();
    setIsLoading(false);
  };

  const handleElectronicFileFolderPathChange = ({ target }: SelectChangeEvent) => {
    setIsLoading(true);
    ApiClient.patch(mediaObject["@id"], {
      body: JSON.stringify({
        electronicFileFolderPath: target.value,
      }),
    })
      .then(() => {
        refreshList();
      })
      .finally(() => {
        resetActiveField();
        setIsLoading(false);
      });
  };

  const isUnlabeledDocument = documentClassLabelAi?.value === "NO_LABEL";
  const legacyVisibilityAndCustomerInfo = mediaObject.scanMetaData?.visibilityAndCustomerInfo === "yes";
  const sendMessageToCustomer =
    mediaObject.scanMetaData?.sendMessageToCustomer ?? legacyVisibilityAndCustomerInfo;
  const showInGallery =
    sendMessageToCustomer || (mediaObject.scanMetaData?.showInGallery ?? legacyVisibilityAndCustomerInfo);

  const isCheckable =
    !!productId &&
    !isLoading &&
    !(isUnlabeledDocument && _.isEmpty(mediaObject.description)) &&
    !(isUnlabeledDocument && ["blank", null].includes(mediaObject.electronicFileFolderPath)) &&
    (!!mediaObject.actionIdentifier || !!mediaObject.scanMetaData?.labelPredicted);

  if (isLoadingActionIdentifiers) {
    return null;
  }

  const isChecked = !!mediaObject.scanMetaData?.checkedDate;
  const hasUnverifiedCaseReferenceWarning =
    mediaObject.scanMetaData?.isFamilyNameInText === false &&
    mediaObject.scanMetaData?.isInsurancePolicyNumberInText !== true &&
    mediaObject.scanMetaData?.isCaseReferenceNumberInText !== true;
  const hasUnverifiedLabelPrediction =
    !!mediaObject.scanMetaData?.labelPredicted &&
    mediaObject.scanMetaData.labelPredicted !== "NO_LABEL" &&
    mediaObject.scanMetaData.isValidDocument === false;
  const hasWarning = hasUnverifiedCaseReferenceWarning || hasUnverifiedLabelPrediction;

  if (isLoadingDocumentClassLabels || isLoadingActionIdentifiers) {
    return null;
  }

  const isShowInGalleryDisabled =
    isLoading ||
    !["showInGallery", ""].includes(activeField) ||
    isChecked ||
    sendMessageToCustomer;
  const isSendMessageToCustomerDisabled =
    isLoading || !["sendMessageToCustomer", ""].includes(activeField) || isChecked;
  const caseLabelWithoutReference = caseLabel ? caseLabel : "Zum Fall";

  return (
    <>
      <TableRow
        sx={[scannedMediaObjectRowStyle.borderLess, hasWarning && scannedMediaObjectRowStyle.warning]}
      >
        <TableCell>
          <div>{dayjs(mediaObject.created).format("DD.MM.YYYY")}</div>
          <div>{dayjs(mediaObject.created).format("HH:mm")} Uhr</div>
        </TableCell>
        <TableCell>
          <DisplayableMediaObject mediaObject={mediaObject} />
        </TableCell>
        <TableCell>
          {!!productId && !!productClassName ? (
            <MuiLink
              component={RouterLink}
              sx={scannedMediaObjectRowStyle.link}
              to={getCaseLink({ productClassName, id: productId })}
              target={"_blank"}
            >
              {!!caseLabel && !!reference ? caseLabel + " - " + reference : caseLabelWithoutReference}
            </MuiLink>
          ) : (
            "Unzugeordnet"
          )}
        </TableCell>
        <TableCell>
          <SearchBar
            searchConfig={{
              indexName: ALGOLIA_INDEX,
            }}
            resultListConfig={{
              itemSize: 330,
              width: "100%",
            }}
            HitComponent={CaseHit}
            onHitClick={onHitClick}
            disabled={isLoading || !["search", ""].includes(activeField) || isChecked}
          />
        </TableCell>
        <TableCell sx={{ verticalAlign: "top" }}>
          <LegalbirdAutoComplete
            disabled={
              !productId ||
              isLoading ||
              !["documentClassLabelAi", ""].includes(activeField) ||
              isChecked
            }
            setValue={handleDocumentClassLabelAiValueChange}
            value={documentClassLabelAi}
            options={documentClassLabelAiOptions}
            label={"Dokumentenart"}
            onFocus={() => setActiveField("documentClassLabelAi")}
            onBlur={resetActiveField}
            isOptionEqualToValue={(option: AutoCompleteOptionWithValue, value: AutoCompleteOptionWithValue) =>
              option.value === value.value
            }
          />
        </TableCell>
        <TableCell sx={{ verticalAlign: "top" }}>
          <LegalbirdAutoComplete
            disabled={
              !productId ||
              isLoading ||
              !["actionIdentifier", ""].includes(activeField) ||
              isChecked
            }
            setValue={handleActionIdentifierValueChange}
            value={actionIdentifier}
            options={actionIdentifierOptions}
            label={"Aktionskenner"}
            onFocus={() => setActiveField("actionIdentifier")}
            onBlur={resetActiveField}
            isOptionEqualToValue={(option: AutoCompleteOptionWithValue, value: AutoCompleteOptionWithValue) =>
              option.value === value.value
            }
            textFieldProps={{
              error: documentClassLabelAi !== null && actionIdentifier === null,
              helperText:
                documentClassLabelAi !== null && actionIdentifier === null
                  ? requiredFieldDefaultFieldBased.required
                  : "",
            }}
          />
        </TableCell>
        <TableCell>
          <Checkbox disabled={!isCheckable} onChange={onCheckboxChange} checked={isChecked} />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          scannedMediaObjectRowStyle.additionalRow,
          { "& td": { paddingTop: "0.5rem" } },
          hasWarning && scannedMediaObjectRowStyle.borderLess,
          hasWarning && scannedMediaObjectRowStyle.warning,
        ]}
      >
        <TableCell>
          <IconButton
            component="a"
            download={mediaObject.originalName}
            href={mediaObject.downloadUrl}
            disabled={isLoading}
            title={"Datei herunterladen"}
          >
            <FileDownloadOutlined />
          </IconButton>
          {mediaObject.fieldname === "bea" &&
          !userHasOneOfTheseRoles(currentUser, [Roles.admin, Roles.productManager]) ? (
            "beA"
          ) : (
            <>
              <IconButton onClick={handleOpenDeleteModal} disabled={isLoading || isChecked}>
                <Delete />
              </IconButton>
              <LegalbirdIoModal
                handleClose={() => setDeleteModalOpen(false)}
                open={deleteModalOpen}
                title={"Dokument löschen"}
                submitButton={
                  <ButtonLoading onClick={handleDeleteMediaObject} isLoading={isLoading} variant={"contained"}>
                    Dokument löschen
                  </ButtonLoading>
                }
              >
                Durch den Klick auf "Dokument löschen" wird das Dokument dauerhaft gelöscht. Grund hierfür können
                beispielsweise falsch eingescannte oder nicht lesbare Dokumente sein.
              </LegalbirdIoModal>
            </>
          )}
        </TableCell>
        {isUnlabeledDocument ? (
          <>
            <TableCell colSpan={2}>
              <TextField
                margin="none"
                label={"Beschreibung"}
                value={fileDescription}
                onChange={({ target }) => setFileDescription(target.value)}
                onBlur={handleSaveDescription}
                disabled={isLoading || !["description", ""].includes(activeField) || isChecked}
                onFocus={() => setActiveField("description")}
              />
            </TableCell>
            <TableCell>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ whiteSpace: "nowrap" }}>
                  Für Mandantschaft sichtbar
                </FormLabel>
                <RadioGroup
                  row
                  name="showInGallery"
                  value={showInGallery.toString()}
                  onChange={handleShowInGalleryChange}
                  onFocus={() => setActiveField("showInGallery")}
                  onBlur={resetActiveField}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label="Ja"
                    disabled={isShowInGalleryDisabled}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="Nein"
                    disabled={isShowInGalleryDisabled}
                  />
                </RadioGroup>
              </FormControl>
            </TableCell>
            <TableCell>
              <FormControl component="fieldset">
                <FormLabel component="legend">Mandantschaft benachrichtigen</FormLabel>
                <RadioGroup
                  row
                  name="sendMessageToCustomer"
                  value={sendMessageToCustomer.toString()}
                  onChange={handleSendMessageToCustomerChange}
                  onFocus={() => setActiveField("sendMessageToCustomer")}
                  onBlur={resetActiveField}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label="Ja"
                    disabled={isSendMessageToCustomerDisabled}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="Nein"
                    disabled={isSendMessageToCustomerDisabled}
                  />
                </RadioGroup>
              </FormControl>
            </TableCell>
            <TableCell>
              <LabeledSelect
                label={"Ablage in Ordner"}
                value={mediaObject.electronicFileFolderPath || "blank"}
                onChange={handleElectronicFileFolderPathChange}
                disabled={isLoading || !["folder", ""].includes(activeField) || isChecked}
                onFocus={() => setActiveField("folder")}
                onBlur={resetActiveField}
              >
                <MenuItem value={"blank"} disabled>
                  {" "}
                </MenuItem>
                <MenuItem value={"Mandantschaft"}>Mandantschaft</MenuItem>
                <MenuItem value={"Gerichtliches Verfahren"}>Gerichtliches Verfahren</MenuItem>
                <MenuItem value={"Außergerichtliches Verfahren"}>Außergerichtliches Verfahren</MenuItem>
                <MenuItem value={"RSV"}>RSV</MenuItem>
                <MenuItem value={"ToDo"}>{"ToDo"}</MenuItem>
                <MenuItem value={"Rechnungen"}>Rechnungen</MenuItem>
                <MenuItem value={"Sonstiges"}>Sonstiges</MenuItem>
              </LabeledSelect>
            </TableCell>
            <TableCell />
          </>
        ) : (
          <TableCell colSpan={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={mediaObject.scanMetaData?.preventDefaultActions || false}
                  onChange={handlePreventDefaultActionsChange}
                  disabled={
                    isLoading ||
                    !["preventDefaultActions", ""].includes(activeField) ||
                    isChecked
                  }
                  onFocus={() => setActiveField("preventDefaultActions")}
                  onBlur={resetActiveField}
                />
              }
              label={"Keine Folge-Aktionen anstoßen (Dokument liegt doppelt vor, oder aus anderen Gründen)"}
            />
          </TableCell>
        )}
      </TableRow>
      {hasUnverifiedLabelPrediction && (
        <TableRow
          sx={[
            scannedMediaObjectRowStyle.additionalRow,
            hasUnverifiedCaseReferenceWarning && scannedMediaObjectRowStyle.borderLess,
            scannedMediaObjectRowStyle.warning,
          ]}
        >
          <TableCell colSpan={7}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FeedbackOutlined sx={{ color: "#db1919", mr: 3, ml: 1 }} />
              <Typography color={"error"} sx={{ flex: 1 }}>
                Der Label-Prozess konnte zwar ein Label erkennen, dieses aber nicht verifizieren. Bitte das Dokument
                und die vorgeschlagene Dokumentenart genau prüfen.
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      )}
      {hasUnverifiedCaseReferenceWarning && (
        <TableRow sx={[scannedMediaObjectRowStyle.additionalRow, scannedMediaObjectRowStyle.warning]}>
          <TableCell colSpan={7}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FeedbackOutlined sx={{ color: "#db1919", mr: 3, ml: 1 }} />
              <Typography color={"error"} sx={{ flex: 1 }}>
                <strong>Manuelle Prüfung zwingend</strong>: Aktenzeichen nicht verifiziert (Nachname, Vers.-Nr. &
                Schaden-/Leistungsnr. fehlen). Bitte Dokumentenzuordnung genau abgleichen und bei Unstimmigkeit Legal informieren.
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
